import { useRef, useEffect } from 'react';
import { COLORS } from '../../constants/theme';
import type { DamageTier } from '../../types/thievery';

interface DamageTierSelectorProps {
  value: DamageTier;
  onChange: (tier: DamageTier) => void;
  allowedTiers?: DamageTier[];
  labels?: Partial<Record<DamageTier, string>>;
}

const ALL_TIERS: DamageTier[] = ['MINOR', 'MEDIUM', 'MASSIVE'];

const DEFAULT_LABEL: Record<DamageTier, string> = {
  MINOR: 'Minor',
  MEDIUM: 'Medium',
  MASSIVE: 'Massive',
};

export function DamageTierSelector({
  value,
  onChange,
  allowedTiers,
  labels,
}: DamageTierSelectorProps) {
  const tiers = allowedTiers ?? ALL_TIERS;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    refs.current = refs.current.slice(0, tiers.length);
  }, [tiers.length]);

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % tiers.length;
      onChange(tiers[next]);
      refs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + tiers.length) % tiers.length;
      onChange(tiers[prev]);
      refs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(tiers[idx]);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-labelledby="damage-tier-label"
      className="flex gap-2"
    >
      {tiers.map((tier, idx) => {
        const isSelected = tier === value;
        const defaultLabel = DEFAULT_LABEL[tier];
        const subLabel = labels?.[tier];
        return (
          <button
            key={tier}
            ref={(el) => { refs.current[idx] = el; }}
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            type="button"
            onClick={() => onChange(tier)}
            onKeyDown={(e) => handleKey(e, idx)}
            className="flex-1 rounded px-3 py-2 text-sm text-left transition-colors"
            style={{
              minHeight: 44,
              backgroundColor: isSelected ? COLORS.sectionHeader : COLORS.card,
              border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
              color: isSelected ? COLORS.text.primary : COLORS.text.secondary,
              outline: 'none',
            }}
          >
            <div className="font-medium">{defaultLabel}</div>
            {subLabel && (
              <div className="text-xs mt-0.5" style={{ color: COLORS.text.tertiary }}>
                {subLabel}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
